import React, { useEffect, useState, useMemo } from 'react';
import { ClientCard } from '../../atoms/ClientCard';
import { useClients } from '../../../hooks/useClients';
import styles from './ClientGrid.module.css';
import { useCursor } from '@/contexts/CursorContext';

interface ClientGridProps {
  className?: string;
  tagFilter?: string;
}

export const ClientGrid: React.FC<ClientGridProps> = ({
  className,
  tagFilter,
}) => {
  const initialDisplayCount = 12; // 4 columns × 3 rows
  const [showAll, setShowAll] = useState(false);

  const { setState } = useCursor();

  const onMouseEnter = () => {
    setState('link');
  };

  const onMouseLeave = () => {
    setState('default');
  };

  // Fetch all clients upfront (single API call), sorted by name
  const { clients, loading, error, isRefetching } = useClients({
    tags: tagFilter,
    page: 1,
    limit: 50,
    sortBy: 'name',
    sortOrder: 'ASC',
  });

  // Filter clients with valid logos first, then slice for display
  const clientsWithLogos = useMemo(
    () => clients.filter((c) => c.id && c.name && c.logo),
    [clients]
  );

  // Memoize displayed clients - show all or just initial count
  const displayedClients = useMemo(
    () =>
      showAll
        ? clientsWithLogos
        : clientsWithLogos.slice(0, initialDisplayCount),
    [clientsWithLogos, showAll, initialDisplayCount]
  );

  const canLoadMore = !showAll && clientsWithLogos.length > initialDisplayCount;

  const handleViewMore = () => {
    setShowAll(true);
  };

  // Reset showAll when tag filter changes
  useEffect(() => {
    setShowAll(false);
  }, [tagFilter]);

  // Show loading state (only on initial load, not refetching)
  if (loading) {
    return (
      <section
        className={`${styles.clientGrid} ${className || ''}`}
        aria-label="Our Client Portfolio"
        aria-busy="true"
      >
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading our amazing clients...</p>
        </div>
      </section>
    );
  }

  // Show error state
  if (error) {
    return (
      <section
        className={`${styles.clientGrid} ${className || ''}`}
        aria-label="Our Client Portfolio"
      >
        <div className={styles.errorState}>
          <p>Error loading clients: {error}</p>
          <button
            onClick={() => window.location.reload()}
            className={styles.retryButton}
          >
            Try Again
          </button>
        </div>
      </section>
    );
  }

  // Show empty state when no clients match the filter
  if (clients.length === 0) {
    const isFiltered = tagFilter && tagFilter !== 'All';
    return (
      <section
        className={`${styles.clientGrid} ${className || ''}`}
        aria-label="Our Client Portfolio"
      >
        <div className={styles.emptyState}>
          <p>
            {isFiltered
              ? `No clients found for "${tagFilter}" category.`
              : 'No clients to display at the moment.'}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      className={`${styles.clientGrid} ${className || ''}`}
      aria-label="Our Client Portfolio"
      aria-busy={isRefetching ? 'true' : 'false'}
    >
      <div className={styles.gridContainer}>
        {displayedClients.map((client) => (
          <ClientCard
            key={client.id}
            name={client.name}
            logoUrl={client.logo || undefined}
            className={styles.gridItem}
          />
        ))}
      </div>
      {canLoadMore && (
        <div className={styles.buttonContainer}>
          <button
            className={styles.viewMoreButton}
            onClick={handleViewMore}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            type="button"
            aria-expanded={showAll}
          >
            View more
          </button>
        </div>
      )}
      {isRefetching && !loading && (
        <div className={styles.refetchingIndicator} aria-live="polite">
          <span className={styles.refetchingDot}></span>
          <span className={styles.visuallyHidden}>Updating client data</span>
        </div>
      )}
    </section>
  );
};
